<template>
  <div
    @dragenter.prevent="handleDragEnter"
    @dragleave.prevent="handleDragLeave"
  >
    <div v-if="isDragOver" class="drag-overlay"></div>

    <div>
      <span class="d-block h4 mb-3">a. Bedruk met uw logo</span>

      <div class="row gx-4 align-items-lg-stretch">
        <div class="col-12 col-lg-6">
          <div
            class="upload"
            :class="{
              'upload--drop': isDragOver,
              'upload--uploading': isUploading,
              'upload--error': uploadError && !isUploading,
            }"
            @click="!isUploading && triggerFileUpload()"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleFileDrop"
          >
            <div v-if="isUploading" class="upload__loading">
              <div
                class="spinner-border spinner-border-sm text-primary"
                role="status"
              >
                <span class="visually-hidden">Loading...</span>
              </div>
              <span class="ms-2">Logo wordt geüpload...</span>
            </div>
            <div v-else-if="uploadError" class="upload__error">
              <i class="icon icon-bell text-danger"></i>
              <div class="upload__content">
                <span class="upload__title text-danger">Upload mislukt</span>
                <span class="upload__subtitle">{{ uploadError }}</span>
                <button
                  @click.stop="retryUpload"
                  class="btn btn--primary btn--sm mt-2"
                >
                  Opnieuw proberen
                </button>
              </div>
            </div>
            <div v-else class="upload__default">
              <i class="icon icon-upload"></i>
              <div class="upload__content">
                <span class="upload__title">Upload uw logo('s)</span>
                <span class="upload__subtitle">
                  Alleen PDF of EPS (maximaal 8MB)
                  <i
                    class="icon icon-info ms-1"
                    :title="'Waarom alleen vector bestanden? Voor de beste printkwaliteit hebben wij schaalbare bestanden nodig.'"
                  ></i>
                </span>
                <div class="upload__help">
                  <small class="text-muted">
                    Sleep bestanden hierheen of klik om te selecteren
                  </small>
                </div>
              </div>
            </div>
          </div>
          <input
            ref="fileInput"
            id="logoFileInput"
            name="logoFileInput"
            type="file"
            multiple
            accept=".eps,.pdf,application/postscript,application/pdf"
            @change="handleFileSelect"
            class="d-none"
          />
        </div>
        <div class="col-12 col-lg-6 order-2 order-lg-0 mt-2 mt-lg-0">
          <textarea
            id="printNotes"
            name="printNotes"
            v-model="printNotes"
            class="form-control upload__notes"
            rows="4"
            placeholder="Notities bedrukken, bijvoorbeeld over je logo's of positie."
            @input="updatePersonalization"
          ></textarea>
        </div>
        <div
          v-if="uploadedLogos.length > 0"
          class="col-auto mt-2 mb-1 d-flex flex-wrap gap-1"
        >
          <TooltipTag
            v-for="(logo, index) in uploadedLogos"
            :hasMaxWidth="true"
            :key="index"
            :name="logo.name"
            @remove="removeLogo(index)"
          />
        </div>
      </div>
    </div>

    <div class="row flex-column g-5 mt-2">
      <PerCardGroup
        v-if="productsByCategory['products'].length > 0"
        title="Producten"
        :products="productsByCategory['products']"
        prefix="product"
        :personalization="productPersonalization"
        @update="updatePersonalization"
      />

      <PerCardGroup
        v-if="productsByCategory['foodDrinks'].length > 0"
        title="Eten & drinken"
        :products="productsByCategory['foodDrinks']"
        prefix="food"
        :personalization="productPersonalization"
        @update="updatePersonalization"
      />

      <PerCardGroup
        v-if="productsByCategory['packaging'].length > 0"
        title="Verpakking"
        :products="productsByCategory['packaging']"
        prefix="packaging"
        :personalization="productPersonalization"
        @update="updatePersonalization"
      />
    </div>

    <div class="row mt-8">
      <div class="col">
        <span class="d-block h4 mb-3">Staffelprijzen bedrukking</span>

        <div
          v-if="!isPrintingValid && printingValidationMsg"
          class="alert alert-danger mb-3"
        >
          <i class="icon icon-bell me-2"></i>
          {{ printingValidationMsg }}
        </div>

        <div v-if="printingTiersLoaded" class="printing-tiers">
          <div class="mb-3 d-flex gap-1" id="pills-tab" role="tablist">
            <button
              v-for="(tiers, method, index) in sortedPrintingTiers"
              :key="method"
              class="per-card__option"
              :class="{ active: index === 0 }"
              :id="`tab-content--${method}-tab`"
              data-bs-toggle="pill"
              :data-bs-target="`#tab-content--${method}`"
              :aria-controls="`tab-content--${method}`"
              :aria-selected="index === 0"
              role="presentation"
            >
              {{ getMethodLabel(method) }}
            </button>
          </div>

          <div class="tab-content" id="pills-tabContent">
            <div
              v-for="(tiers, method, index) in sortedPrintingTiers"
              :key="method"
              class="mb-3 tab-pane fade"
              :class="{ 'active show': index === 0 }"
              :id="`tab-content--${method}`"
              role="tabpanel"
              aria-labelledby="pills-profile-tab"
              tabindex="0"
            >
              <div class="table-responsive">
                <table class="table table-sm printing-tiers-table">
                  <thead>
                    <tr>
                      <th class="fw-regular table__fit-width">
                        Totaal aantal producten
                      </th>
                      <th class="fw-regular">Prijs per stuk (ex. btw)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="tier in tiers"
                      :key="`${method}-${tier.quantity_min}`"
                      :class="{
                        'table-active': isCurrentTier(method, tier),
                      }"
                    >
                      <td class="table__fit-width">
                        {{ tier.quantity_min }}
                        <span v-if="tier.quantity_max !== 2147483647">
                          – {{ tier.quantity_max }}
                        </span>
                        <span v-else>+</span>
                      </td>
                      <td>€{{ formatPrice(tier.price_per_unit) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <span v-else class="d-block fs-xs text-gray-700">Laden…</span>
      </div>
    </div>

    <div class="mt-8">
      <span class="d-block h4 mb-3"
        >b. Voeg een gepersonaliseerde kaart toe</span
      >

      <div v-if="cardsLoading" class="row g-2">
        <div class="col-6 col-md-4" v-for="n in 6" :key="n">
          <div class="skeleton skeleton--product-card"></div>
        </div>
      </div>

      <div v-else-if="cardsError" class="alert alert-danger">
        <strong>Fout:</strong> {{ cardsError }}
        <button
          @click="retryLoadCards"
          class="btn btn-sm btn-outline-danger ms-2"
        >
          Opnieuw proberen
        </button>
      </div>

      <div v-else-if="cards.length === 0" class="text-center py-4">
        <div class="text-muted">
          <i class="icon icon-card fs-1 d-block mb-2"></i>
          <p class="mb-1">Geen gepersonaliseerde kaarten beschikbaar</p>
          <small
            >Er zijn momenteel geen kaarten beschikbaar in deze
            categorie.</small
          >
        </div>
      </div>

      <div
        v-else
        class="row g-2"
        role="grid"
        aria-labelledby="personalized-cards-section"
      >
        <div
          class="col-6 col-md-4"
          v-for="card in cardsWithDynamicPrices"
          :key="card.id"
          role="gridcell"
        >
          <Card
            :product="card"
            :count="0"
            :isPackaging="false"
            :isPersonalizedCard="true"
            :packageType="packageType"
            :isImageClickable="true"
            :selected="isSelectedCard(card)"
            @select="selectCard(card)"
            @open-modal="openProductDetailPopup(card)"
          />
        </div>
      </div>
    </div>
  </div>

  <!-- Popup -->
  <ProductDetailModal
    v-if="showProductDetailModal && productDetailModalProduct"
    :product="productDetailModalProductWithPrice"
    :isPackaging="false"
    :isPersonalizedCard="true"
    :packageType="packageType"
    :selected="isSelectedCard(productDetailModalProduct)"
    @select="selectCard(productDetailModalProduct)"
    @close-modal="showProductDetailModal = false"
  />
</template>

<script>
import PerCardGroup from '../components/PerCardGroup.vue';
import TooltipTag from '../components/TooltipTag.vue';
import { Tooltip } from 'bootstrap';
import { decodeHtml } from '../utils/productCountUtils.js';
import {
  formatPrice as formatPriceUtil,
  calculatePrintingCounts as calculatePrintingCountsUtil,
  calculatePrintingCosts as calculatePrintingCostsUtil,
  getCardUnitPrice,
} from '../utils/priceUtils.js';
import Card from '../templates/Card.vue';
import ProductDetailModal from '../modals/ProductDetailModal.vue';

export default {
  name: 'Personalization',
  components: { ProductDetailModal, PerCardGroup, TooltipTag, Card },
  props: {
    data: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    endpoint: { type: String, required: true },
    packageType: { type: String, default: 'large' },
    mode: { type: String, default: null },
  },
  emits: [
    'update',
    'next',
    'go-to-step',
    'reset-package',
    'start',
    'increase-product',
    'decrease-product',
    'add-to-cart',
    'remove-product',
    'remove-food',
    'remove-packaging',
    'select-base',
    'printing-updated',
    'remove-base-package',
  ],
  data() {
    return {
      uploadedLogos: [],
      logoAttachments: [],
      printNotes: '',
      productPersonalization: {},
      isDragOver: false,
      cards: [],
      cardsLoading: false,
      cardsError: null,
      showProductDetailModal: false,
      productDetailModalProduct: {},
      printingTiers: {},
      printingTiersLoaded: false,
      printingCounts: { sticker: 0, zeefdruk: 0, graveren: 0 },
      printingBreakdown: { methods: {}, totalExVat: 0 },
      printsPerPakketBreakdown: {
        sticker: { qty: 0, unit: 0, total: 0 },
        zeefdruk: { qty: 0, unit: 0, total: 0 },
        graveren: { qty: 0, unit: 0, total: 0 },
        totalExVat: 0,
      },
      isPrintingCalculating: false,
      printingMinQty: 0,
      totalPrintQty: 0,
      isPrintingValid: true,
      printingValidationMsg: '',
      printingDebounceTimer: null,
      isUploading: false,
      uploadError: null,
      retryFiles: [],
      productsByCategory: {
        products: [],
        foodDrinks: [],
        packaging: [],
      },
      // Performance optimizations
      printingCostsCache: new Map(),
      lastCalculatedCounts: null,
      pendingUpdates: new Set(),
      _productsCache: {},
      cardPrices: {}, // Cache for dynamic card prices based on quantity
    };
  },
  watch: {
    'data.products': {
      handler() {
        this.reloadProductsByCategory();
        this.initializePersonalization();
      },
      immediate: true,
      deep: false, // Optimize for better performance
    },
    'data.foodDrinks': {
      handler() {
        this.reloadProductsByCategory();
        this.initializePersonalization();
      },
      immediate: true,
      deep: false, // Optimize for better performance
    },
    'data.packaging': {
      handler() {
        this.reloadProductsByCategory();
        this.initializePersonalization();
      },
      immediate: true,
      deep: false, // Optimize for better performance
    },
    'data.personalization': {
      handler: 'initializePersonalization',
      immediate: true,
      deep: false, // Optimize for better performance
    },
    'data.packageQuantity': {
      handler() {
        this.debouncedRecalculatePrinting();
        this.updateCardPrices();
      },
      immediate: false,
    },
    'data.quantity': {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.updateCardPrices();
        }
      },
      immediate: false,
    },
    productPersonalization: {
      handler: 'onProductPersonalizationChanged',
      deep: false, // Changed from true to false for better performance
      immediate: false,
    },
    cards: {
      handler() {
        this.updateCardPrices();
      },
      immediate: false,
    },
    uploadedLogos: {
      handler: 'reinitializeTooltips',
      deep: true,
    },
    // Watch for package configuration changes to reload logos
    '$parent.packageConfig': {
      handler(newConfig, oldConfig) {
        if (
          newConfig &&
          oldConfig &&
          this._hasConfigChanged(newConfig, oldConfig)
        ) {
          this.clearCaches();
          this.$nextTick(() => {
            this.loadLogosFromPackageConfig();
          });
        }
      },
      deep: true,
    },
  },
  computed: {
    selectedCard() {
      return this.data.personalization?.selectedCard || null;
    },

    hasSelectedCard() {
      return !!this.selectedCard;
    },

    selectedCardPrice() {
      if (!this.selectedCard) return null;
      return this.selectedCard.price.toFixed(2).replace('.', ',');
    },

    packageQuantity() {
      // Probeer verschillende locaties voor packageQuantity
      const quantity =
        this.data.packageQuantity ||
        this.data.quantity ||
        this.data.package?.quantity ||
        1;

      return quantity;
    },

    cardPricesKeys() {
      return Object.keys(this.cardPrices);
    },

    cardsWithDynamicPrices() {
      this.cardPricesKeys;

      return this.cards.map((card) => {
        const dynamicPrice = this.cardPrices[card.id];
        const finalPrice =
          dynamicPrice !== undefined ? dynamicPrice : card.price;
        return {
          ...card,
          price: finalPrice,
        };
      });
    },

    productDetailModalProductWithPrice() {
      if (!this.productDetailModalProduct) {
        return null;
      }

      // Ensure modal shows the correct dynamic price
      const cardWithPrice = this.cardsWithDynamicPrices.find(
        (card) => card.id === this.productDetailModalProduct.id,
      );

      return cardWithPrice || this.productDetailModalProduct;
    },

    sortedPrintingTiers() {
      // Sorteer de drukwerk methoden in de gewenste volgorde
      const methodOrder = ['sticker', 'zeefdruk', 'graveren'];
      const sortedTiers = {};

      methodOrder.forEach((method) => {
        if (this.printingTiers[method]) {
          sortedTiers[method] = this.printingTiers[method];
        }
      });

      return sortedTiers;
    },

    // Memoized product categories for better performance
    memoizedProductsByCategory() {
      return {
        products: this.productsByCategory.products,
        foodDrinks: this.productsByCategory.foodDrinks,
        packaging: this.productsByCategory.packaging,
      };
    },

    // Memoized personalization counts
    personalizationCounts() {
      return Object.values(this.productPersonalization).filter(
        (personalization) =>
          personalization &&
          (personalization.sticker ||
            personalization.zeefdruk ||
            personalization.graveren),
      ).length;
    },
  },
  mounted() {
    this.initializeTooltips();
    this.loadCards();
    this.loadPrintingTiers(); // Weer ingeschakeld

    // Get all products - use the new method
    this.reloadProductsByCategory();

    // Initialize personalization immediately after products are loaded
    this.$nextTick(() => {
      this.initializePersonalization();
    });

    // Luister naar reload event
    this.setupEventListeners();

    // Trigger eerste berekening na een korte delay
    setTimeout(() => {
      this.recalculatePrinting(); // Directe berekening voor eerste keer
    }, 2000);

    // Also try to load logos when component mounts
    this.$nextTick(() => {
      this.loadLogosFromPackageConfig();
      this.updateCardPrices();
    });
  },
  methods: {
    formatPrice(price) {
      return formatPriceUtil(price);
    },
    // @todo replace this with cardUtils.js function
    async loadCards() {
      try {
        this.cardsLoading = true;
        this.cardsError = null;

        // Gepersonaliseerde kaarten worden altijd getoond, ongeacht de brievenbus keuze
        const url = '/api/products/gepersonaliseerde-kaarten';

        const res = await fetch(url, {
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error('Failed to load cards');
        let cards = await res.json();
        cards = cards.items;

        if (Array.isArray(cards)) {
          cards = cards.filter((card) => this.isValidCard(card));
          this.cards = cards;
          // Update prices after loading cards
          this.$nextTick(() => {
            this.updateCardPrices();
          });
        } else {
          this.cards = [];
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error loading cards:', error);
        this.cardsError = 'Kon kaarten niet laden. Probeer het opnieuw.';
        this.cards = [];
      } finally {
        this.cardsLoading = false;
      }
    },

    async retryLoadCards() {
      await this.loadCards();
    },

    isSelectedCard(card) {
      return this.selectedCard?.id === card.id;
    },

    isValidCard(card) {
      return (
        card &&
        card.id &&
        card.name &&
        typeof card.price === 'number' &&
        card.price >= 0
      );
    },
    setupEventListeners() {
      document.addEventListener('s7_reload_products', (e) => {
        if (e.detail?.packageType) {
          this.loadCards();
        }
      });
    },

    selectCard(card) {
      if (!this.isValidCard(card)) {
        return;
      }

      const isCurrentlySelected = this.isSelectedCard(card);
      const cardWithPrice =
        this.cardsWithDynamicPrices.find((c) => c.id === card.id) || card;

      // Use the original card's price (base WooCommerce price), not the dynamic tier price
      // Find the original card from the cards array to get the true base price
      const originalCard = this.cards.find((c) => c.id === card.id) || card;
      const basePrice = originalCard.price || 1.4;

      const selectedCard = isCurrentlySelected
        ? null
        : {
            ...cardWithPrice,
            permalink: cardWithPrice.permalink || card.permalink,
            tax_class: cardWithPrice.tax_class || card.tax_class || 'inherit',
            originalPrice: basePrice,
          };

      if (this.data.personalization) {
        this.data.personalization.selectedCard = selectedCard;
      }
    },

    openProductDetailPopup(product) {
      const cardWithPrice =
        this.cardsWithDynamicPrices.find((card) => card.id === product.id) ||
        product;
      this.productDetailModalProduct = cardWithPrice;
      this.showProductDetailModal = true;
    },

    async updateCardPrices() {
      if (!this.cards.length || !this.packageQuantity) {
        this.cardPrices = {};
        return;
      }

      try {
        const tierPrice = await getCardUnitPrice(this.packageQuantity);
        const newPrices = {};

        this.cards.forEach((card) => {
          newPrices[card.id] = tierPrice > 0 ? tierPrice : card.price;
        });

        this.cardPrices = newPrices;
      } catch {
        const fallbackPrices = {};
        this.cards.forEach((card) => {
          fallbackPrices[card.id] = card.price;
        });
        this.cardPrices = fallbackPrices;
      }
    },

    initializeTooltips() {
      document
        .querySelectorAll('[data-bs-toggle="tooltip"]')
        .forEach((el) => new Tooltip(el));
    },
    reinitializeTooltips() {
      this.$nextTick(this.initializeTooltips);
    },
    hideAllTooltips() {
      document.querySelectorAll('.tooltip').forEach((tooltip) => {
        tooltip.remove();
      });
    },
    initializePersonalization() {
      if (this.data.personalization?.productPersonalization) {
        this.productPersonalization = {
          ...this.data.personalization.productPersonalization,
        };
      }

      if (this.data.personalization?.printNotes) {
        this.printNotes = this.data.personalization.printNotes;
      }

      // Load uploaded logos if available
      if (this.data.personalization?.logos) {
        this.uploadedLogos = [...this.data.personalization.logos];
      }

      // Load logo attachments if available
      if (this.data.personalization?.logoAttachments) {
        this.logoAttachments = [...this.data.personalization.logoAttachments];
      }

      // Try to load logos from package configuration (when returning from cart)
      this.loadLogosFromPackageConfig();

      const allProducts = [
        ...(this.data.products || []),
        ...(this.data.foodDrinks || []),
        ...([this.data.packagingProduct] || []),
        ...(this.data.basePackage?.products || []),
        ...(this.data.basePackage?.foodDrinks || []),
      ];

      allProducts.forEach((product) => {
        if (!product?.id) {
          return;
        }

        if (!this.productPersonalization?.[product.id]) {
          this.productPersonalization[product.id] = {
            sticker: false,
            zeefdruk: false,
            graveren: false,
          };
        }

        ['sticker', 'zeefdruk', 'graveren'].forEach((option) => {
          if (!this.canPersonalizeOption(product, option)) {
            this.productPersonalization[product.id][option] = false;
          }
        });
      });
    },
    reloadProductsByCategory() {
      // Reload products into productsByCategory whenever data changes
      this.productsByCategory['products'] = this.getProductsByCategory(
        'products',
      ).filter((p) => p.id !== this.data?.packagingProduct?.id);
      this.productsByCategory['foodDrinks'] =
        this.getProductsByCategory('foodDrinks');
      this.productsByCategory['packaging'] =
        this.getProductsByCategory('packaging');
    },
    getProductsByCategory(category) {
      // Use memoization for better performance
      const cacheKey = `${category}_${JSON.stringify(this.data)}`;
      if (this._productsCache && this._productsCache[cacheKey]) {
        return this._productsCache[cacheKey];
      }

      let products = [];

      if (
        this.data.basePackage &&
        this.data.basePackage[category] &&
        Array.isArray(this.data.basePackage[category])
      ) {
        products = this.data.basePackage[category];
      }

      if (
        category === 'products' &&
        this.data.products &&
        Array.isArray(this.data.products)
      ) {
        const selectedProducts = this.data.products.filter((p) => p.count > 0);
        products = [...products, ...selectedProducts];
      }

      if (
        category === 'packaging' &&
        (this.data.packaging || this.data.packagingProduct)
      ) {
        const selectedProducts = [];
        if (this.data?.packaging) selectedProducts.push(this.data?.packaging);
        if (this.data?.packagingProduct)
          selectedProducts.push(this.data?.packagingProduct);
        products = [...products, ...selectedProducts];
      }

      if (
        category === 'foodDrinks' &&
        this.data.foodDrinks &&
        Array.isArray(this.data.foodDrinks)
      ) {
        const selectedFoodDrinks = this.data.foodDrinks.filter(
          (f) => f.count > 0,
        );
        products = [...products, ...selectedFoodDrinks];
      }

      // Remove duplicates based on product ID using Map for better performance
      const uniqueProductsMap = new Map();
      products.forEach((product) => {
        if (product.id && !uniqueProductsMap.has(product.id)) {
          uniqueProductsMap.set(product.id, product);
        }
      });

      const result = Array.from(uniqueProductsMap.values()).map((product) => ({
        ...product,
        name: decodeHtml(product.name || product.title || ''),
        title: decodeHtml(product.title || product.name || ''),
      }));

      // Cache the result
      if (!this._productsCache) {
        this._productsCache = {};
      }
      this._productsCache[cacheKey] = result;

      return result;
    },
    canPersonalizeOption(product, optionType) {
      if (!product?.attributes) return false;

      const attributeMapping = {
        sticker: 'pa_sticker',
        zeefdruk: 'pa_zeefdruk',
        graveren: 'pa_graveren',
      };
      const attributeName = attributeMapping[optionType];
      if (!attributeName) return false;

      const attribute = product.attributes.find(
        (attr) => attr.name === attributeName,
      );
      if (!attribute) return false;

      const value =
        attribute.value ||
        attribute.options?.[0] ||
        attribute.option ||
        attribute.name ||
        '';
      const stringValue = value.toString().toLowerCase().trim();

      return ['ja', 'yes', 'true', '1'].includes(stringValue);
    },
    updatePersonalization(updatedProductPersonalization = null) {
      if (updatedProductPersonalization) {
        this.productPersonalization = updatedProductPersonalization;
      }

      const printingData = this.isPrintingValid
        ? {
            counts: this.printingCounts,
            breakdown: this.printingBreakdown,
            total_ex_vat: this.printingBreakdown.totalExVat,
            // Add per-package printing data for correct pricing
            per_package: {
              counts: this.printsPerPakket,
              breakdown: this.printsPerPakketBreakdown,
              total_ex_vat: this.printsPerPakketBreakdown?.totalExVat || 0,
            },
          }
        : null;

      this.$emit('update', {
        personalization: {
          logos: this.uploadedLogos,
          logoAttachments: this.logoAttachments,
          printNotes: this.printNotes,
          productPersonalization: this.productPersonalization,
          selectedCard: this.data.personalization?.selectedCard || null,
          printing: printingData,
        },
      });

      // Save logos to package config whenever personalization updates
      this.saveLogosToPackageConfig();
    },
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },
    retryUpload() {
      if (this.retryFiles.length > 0) {
        this.processFiles(this.retryFiles);
      } else {
        this.triggerFileUpload();
      }
    },
    handleFileSelect(event) {
      this.processFiles(Array.from(event.target.files));
    },
    handleFileDrop(event) {
      this.processFiles(Array.from(event.dataTransfer.files));
      this.isDragOver = false;
    },
    async processFiles(files) {
      this.uploadError = null;
      this.retryFiles = [];

      const validationResults = files.map((file) => ({
        file,
        validation: this.validateFile(file),
      }));

      const validFiles = validationResults
        .filter((result) => result.validation.valid)
        .map((result) => result.file);

      const invalidFiles = validationResults.filter(
        (result) => !result.validation.valid,
      );

      if (invalidFiles.length > 0 && validFiles.length === 0) {
        const firstError = invalidFiles[0].validation.message;
        this.uploadError = firstError;

        if (invalidFiles.length > 1) {
          this.uploadError += ` (${invalidFiles.length} bestanden hebben problemen)`;
        }

        return;
      }

      if (validFiles.length === 0) {
        return;
      }

      if (invalidFiles.length > 0) {
        const firstError = invalidFiles[0].validation.message;
        this.uploadError = firstError;

        if (invalidFiles.length > 1) {
          this.uploadError += ` (${invalidFiles.length} bestanden hebben problemen)`;
        }
      }

      this.isUploading = true;
      this.retryFiles = [...validFiles];

      try {
        let successCount = 0;
        const failedFiles = [];

        for (const file of validFiles) {
          try {
            await this.uploadFile(file);
            successCount++;
          } catch (error) {
            console.error('Upload failed for file:', file.name, error);
            failedFiles.push({
              name: file.name,
              error: error.message,
            });
          }
        }

        if (successCount < validFiles.length) {
          const failedCount = validFiles.length - successCount;
          const uploadError =
            failedCount === 1
              ? failedFiles[0].error
              : `${failedCount} bestand(en) konden niet worden geüpload. Probeer opnieuw.`;

          if (invalidFiles.length > 0) {
            this.uploadError = `${this.uploadError} Daarnaast konden ${failedCount} geldige bestand(en) niet worden geüpload.`;
          } else {
            this.uploadError = uploadError;
          }
        }
      } catch (error) {
        this.uploadError =
          'Er ging iets mis bij het uploaden. Probeer het opnieuw.';
      } finally {
        this.isUploading = false;
        this.$refs.fileInput.value = '';
      }
    },
    validateFile(file) {
      const maxSize = 8 * 1024 * 1024;
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      const extension = file.name.split('.').pop().toLowerCase();
      const mimeType = file.type;

      if (file.size > maxSize) {
        return {
          valid: false,
          message: `Uw bestand is ${fileSizeMB}MB. Het maximum is 8MB. Comprimeer uw logo of neem contact met ons op.`,
        };
      }
      const imageError =
        'Afbeeldingsbestanden zijn niet geschikt voor drukwerk. Upload een vectorbestand (PDF of EPS).';
      const officeError =
        'Office bestanden worden niet ondersteund. Exporteer uw logo als PDF.';

      const fileTypeErrors = {
        ai: 'Adobe Illustrator bestanden (.ai) worden niet ondersteund. Exporteer uw logo als PDF of EPS.',
        jpg: imageError,
        jpeg: imageError,
        png: imageError,
        gif: imageError,
        bmp: imageError,
        tiff: imageError,
        'image/jpeg': imageError,
        'image/png': imageError,
        'image/gif': imageError,
        'image/bmp': imageError,
        'image/tiff': imageError,
        doc: officeError,
        docx: officeError,
        ppt: officeError,
        pptx: officeError,
        xls: officeError,
        xlsx: officeError,
        'application/msword': officeError,
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          officeError,
        'application/vnd.ms-powerpoint': officeError,
        'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          officeError,
        svg: 'SVG bestanden worden niet ondersteund. Converteer naar PDF of EPS.',
        'image/svg+xml':
          'SVG bestanden worden niet ondersteund. Converteer naar PDF of EPS.',
        psd: 'Photoshop bestanden (.psd) worden niet ondersteund. Exporteer uw logo als PDF of EPS.',
        sketch:
          'Sketch bestanden worden niet ondersteund. Exporteer uw logo als PDF of EPS.',
        fig: 'Figma bestanden worden niet ondersteund. Exporteer uw logo als PDF of EPS.',
        xd: 'Adobe XD bestanden worden niet ondersteund. Exporteer uw logo als PDF of EPS.',
      };

      if (fileTypeErrors[extension]) {
        return {
          valid: false,
          message: fileTypeErrors[extension],
        };
      }

      if (fileTypeErrors[mimeType]) {
        return {
          valid: false,
          message: fileTypeErrors[mimeType],
        };
      }
      const validTypes = [
        'application/pdf',
        'application/postscript',
        'application/eps',
        'application/x-eps',
      ];

      const validExtensions = ['pdf', 'eps'];

      if (
        !validTypes.includes(mimeType) &&
        !validExtensions.includes(extension)
      ) {
        return {
          valid: false,
          message: `Dit bestandstype (.${extension}) wordt niet ondersteund. Upload een PDF of EPS bestand.`,
        };
      }

      return { valid: true };
    },

    isValidFile(file) {
      return this.validateFile(file).valid;
    },
    async uploadFile(file) {
      if (!window.S7LogoUpload) {
        throw new Error(
          'Upload functionaliteit niet beschikbaar. Probeer de pagina te verversen.',
        );
      }

      const formData = new FormData();
      formData.append('action', 's7_upload_logo');
      formData.append('nonce', window.S7LogoUpload.nonce);
      formData.append('file', file);

      try {
        const response = await fetch(window.S7LogoUpload.ajax_url, {
          method: 'POST',
          body: formData,
          credentials: 'same-origin',
        });

        if (!response.ok) {
          if (response.status === 0 || !response.status) {
            throw new Error(
              'Upload mislukt door verbindingsprobleem. Controleer uw internet en probeer opnieuw.',
            );
          }

          if (response.status === 413) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
            throw new Error(
              `Uw bestand is ${fileSizeMB}MB. Het maximum is 8MB. Comprimeer uw logo of neem contact met ons op.`,
            );
          }

          if (response.status >= 500) {
            throw new Error(
              'Er ging iets mis op de server. Probeer het over een minuut opnieuw of neem contact op.',
            );
          }

          throw new Error(
            'Er ging iets mis bij het uploaden. Probeer het opnieuw.',
          );
        }

        const result = await response.json();

        if (!result.success) {
          if (result.data && result.data.code) {
            const errorMessages = {
              file_too_large:
                'Uw bestand is te groot. Het maximum is 8MB. Comprimeer uw logo of neem contact met ons op.',
              invalid_mime:
                'Dit bestandstype wordt niet ondersteund. Upload een PDF of EPS bestand.',
              invalid_extension:
                'Dit bestandstype wordt niet ondersteund. Upload een PDF of EPS bestand.',
              invalid_file_type:
                result.data.message ||
                'Dit bestandstype wordt niet ondersteund. Upload een PDF of EPS bestand.',
              upload_error:
                'Upload fout: ' + (result.data.message || 'Onbekende fout'),
              wp_upload_error:
                'WordPress upload fout: ' +
                (result.data.message || 'Onbekende fout'),
              attachment_creation_failed:
                'Kon bestand niet verwerken: ' +
                (result.data.message || 'Onbekende fout'),
              server_error:
                'Server fout: ' + (result.data.message || 'Onbekende fout'),
              invalid_nonce:
                'Beveiligingscontrole mislukt. Ververs de pagina en probeer opnieuw.',
              no_file: 'Geen bestand ontvangen. Probeer opnieuw.',
            };

            throw new Error(
              errorMessages[result.data.code] ||
                result.data.message ||
                'Upload mislukt',
            );
          }

          throw new Error(
            result.data?.message || result.message || 'Upload mislukt',
          );
        }

        this.uploadedLogos.push({
          name: result.data.filename,
          attachment_id: result.data.attachment_id,
          size: result.data.size,
          type: result.data.mime,
          url: result.data.url,
          title: result.data.title,
        });

        this.logoAttachments.push({
          id: result.data.attachment_id,
          filename: result.data.filename,
          url: result.data.url,
        });

        localStorage.setItem(
          's7_logo_attachments',
          JSON.stringify(this.logoAttachments),
        );

        this.saveLogosToPackageConfig();

        window.dispatchEvent(
          new CustomEvent('s7-logo-updated', {
            detail: { attachments: this.logoAttachments },
          }),
        );

        // this.updatePersonalization();
      } catch (error) {
        throw new Error(
          error.message || 'Upload mislukt. Probeer het opnieuw.',
        );
      }
    },

    saveLogosToPackageConfig() {
      let packageConfig = null;

      if (this.$parent && this.$parent.packageConfig) {
        packageConfig = this.$parent.packageConfig;
      } else if (this.data) {
        packageConfig = {
          products: this.data.products || [],
          foodDrinks: this.data.foodDrinks || [],
          packaging: this.data.packaging || this.data.packagingProduct || null,
          personalization: this.data.personalization || {},
        };
      }

      if (packageConfig) {
        const packageKey = this.generatePackageKey(packageConfig);

        const packageLogos = {
          packageKey,
          logos: [...this.logoAttachments],
          timestamp: Date.now(),
        };

        localStorage.setItem(
          `s7_package_logos_${packageKey}`,
          JSON.stringify(packageLogos),
        );

        const packageSessionKey = this.generatePackageSessionKey(packageConfig);
        localStorage.setItem(
          `s7_package_session_logos_${packageSessionKey}`,
          JSON.stringify(packageLogos),
        );
      } else {
        // No package configuration found, cannot save logos
      }
    },

    generatePackageKey(config) {
      // Create a unique key based on package configuration
      const keyData = {
        products:
          config.products?.map((p) => ({ id: p.id, quantity: p.quantity })) ||
          [],
        foodDrinks:
          config.foodDrinks?.map((f) => ({ id: f.id, quantity: f.quantity })) ||
          [],
        packaging: config.packaging?.id || null,
        personalization: {
          printTypes: config.personalization?.printTypes || [],
          printNotes: config.personalization?.printNotes || '',
        },
      };

      const jsonString = JSON.stringify(keyData);

      let hash = 0;
      for (let i = 0; i < jsonString.length; i++) {
        const char = jsonString.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
      }

      const packageKey = Math.abs(hash).toString(36);
      return packageKey;
    },

    generateSessionKey() {
      // Generate a session key that persists during the browser session
      let sessionKey = sessionStorage.getItem('s7_session_id');
      if (!sessionKey) {
        sessionKey =
          'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('s7_session_id', sessionKey);
      }
      return sessionKey;
    },

    generatePackageSessionKey(config) {
      // Generate a session key specific to this package configuration
      const packageKey = this.generatePackageKey(config);
      const sessionKey = this.generateSessionKey();
      return `${sessionKey}_${packageKey}`;
    },

    loadLogosFromPackageConfig() {
      let packageConfig = null;

      if (this.$parent && this.$parent.packageConfig) {
        packageConfig = this.$parent.packageConfig;
      } else if (this.data) {
        packageConfig = {
          products: this.data.products || [],
          foodDrinks: this.data.foodDrinks || [],
          packaging: this.data.packaging || this.data.packaging || null,
          personalization: this.data.personalization || {},
        };
      }

      if (packageConfig) {
        const packageKey = this.generatePackageKey(packageConfig);
        const storedLogos = localStorage.getItem(
          `s7_package_logos_${packageKey}`,
        );

        if (storedLogos) {
          try {
            const packageLogos = JSON.parse(storedLogos);
            const isRecent =
              Date.now() - packageLogos.timestamp < 24 * 60 * 60 * 1000;

            if (
              isRecent &&
              packageLogos.logos &&
              packageLogos.logos.length > 0
            ) {
              this.logoAttachments = [...packageLogos.logos];
              this.uploadedLogos = packageLogos.logos.map((logo) => ({
                name: logo.filename,
                attachment_id: logo.id,
                size: 0,
                type: 'application/pdf',
                url: logo.url,
                title: logo.filename,
              }));

              localStorage.setItem(
                's7_logo_attachments',
                JSON.stringify(this.logoAttachments),
              );

              window.dispatchEvent(
                new CustomEvent('s7-logo-updated', {
                  detail: { attachments: this.logoAttachments },
                }),
              );
            }
          } catch (error) {
            console.error('Error loading package logos:', error);
          }
        } else {
          const packageSessionKey =
            this.generatePackageSessionKey(packageConfig);
          const packageSessionStoredLogos = localStorage.getItem(
            `s7_package_session_logos_${packageSessionKey}`,
          );

          if (packageSessionStoredLogos) {
            try {
              const packageSessionLogos = JSON.parse(packageSessionStoredLogos);

              if (
                packageSessionLogos.logos &&
                packageSessionLogos.logos.length > 0
              ) {
                this.logoAttachments = [...packageSessionLogos.logos];
                this.uploadedLogos = packageSessionLogos.logos.map((logo) => ({
                  name: logo.filename,
                  attachment_id: logo.id,
                  size: 0,
                  type: 'application/pdf',
                  url: logo.url,
                  title: logo.filename,
                }));

                localStorage.setItem(
                  's7_logo_attachments',
                  JSON.stringify(this.logoAttachments),
                );

                window.dispatchEvent(
                  new CustomEvent('s7-logo-updated', {
                    detail: { attachments: this.logoAttachments },
                  }),
                );
              }
            } catch (error) {
              console.error(
                'Error loading logos with package session key:',
                error,
              );
            }
          }
        }
      } else {
      }

      // Clean up old package logos (older than 7 days)
      this.cleanupOldPackageLogos();
    },

    cleanupOldPackageLogos() {
      const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

      // Get all package logo keys
      const packageKeys = Object.keys(localStorage).filter((key) =>
        key.startsWith('s7_package_logos_'),
      );

      // Get all package session logo keys
      const packageSessionKeys = Object.keys(localStorage).filter((key) =>
        key.startsWith('s7_package_session_logos_'),
      );

      // Clean up old package logos
      packageKeys.forEach((key) => {
        try {
          const packageLogos = JSON.parse(localStorage.getItem(key));
          if (packageLogos.timestamp < sevenDaysAgo) {
            localStorage.removeItem(key);
          }
        } catch (error) {
          // Remove invalid entries
          localStorage.removeItem(key);
        }
      });

      // Clean up old package session logos
      packageSessionKeys.forEach((key) => {
        try {
          const packageSessionLogos = JSON.parse(localStorage.getItem(key));
          if (packageSessionLogos.timestamp < sevenDaysAgo) {
            localStorage.removeItem(key);
          }
        } catch (error) {
          // Remove invalid entries
          localStorage.removeItem(key);
        }
      });
    },

    async removeLogo(index) {
      this.hideAllTooltips();

      const logo = this.uploadedLogos[index];

      if (logo.attachment_id) {
        try {
          // For now, we'll just remove from local state
          // In the future, we could implement a delete endpoint
        } catch (error) {
          console.error('Error removing logo:', error);
        }
      }

      this.uploadedLogos.splice(index, 1);
      this.logoAttachments.splice(index, 1);

      // Update localStorage after removing logo
      localStorage.setItem(
        's7_logo_attachments',
        JSON.stringify(this.logoAttachments),
      );

      // Also update package-specific storage
      this.saveLogosToPackageConfig();

      // Dispatch event for checkout to pick up
      window.dispatchEvent(
        new CustomEvent('s7-logo-updated', {
          detail: { attachments: this.logoAttachments },
        }),
      );

      // this.updatePersonalization();
    },
    handleDragEnter() {
      this.isDragOver = true;
    },
    handleDragOver() {
      this.isDragOver = true;
    },
    handleDragLeave(event) {
      if (!event.relatedTarget || !this.$el.contains(event.relatedTarget)) {
        this.isDragOver = false;
      }
    },
    async loadPrintingTiers() {
      try {
        this.printingTiersLoaded = false;

        // Haal tiers op
        const tiersUrl = '/api/printing/tiers';
        const tiersRes = await fetch(tiersUrl, {
          credentials: 'same-origin',
        });
        if (!tiersRes.ok) throw new Error('Failed to load printing tiers');
        this.printingTiers = await tiersRes.json();

        // Haal minimum quantity op
        const minQtyUrl = '/api/printing/min-qty';
        const minQtyRes = await fetch(minQtyUrl, {
          credentials: 'same-origin',
        });
        if (minQtyRes.ok) {
          const minQtyData = await minQtyRes.json();
          this.printingMinQty = minQtyData.min_quantity || 0;
        }
      } catch (error) {
        console.error('Error loading printing data:', error);
        this.printingTiers = {};
        this.printingMinQty = 0;
      } finally {
        this.printingTiersLoaded = true;
      }
    },
    getMethodLabel(method) {
      switch (method) {
        case 'sticker':
          return 'Sticker';
        case 'zeefdruk':
          return 'Zeefdruk';
        case 'graveren':
          return 'Graveren';
        default:
          return method;
      }
    },
    isCurrentTier(method, tier) {
      const currentQuantity = this.printingCounts[method] || 0;
      return (
        currentQuantity >= tier.quantity_min &&
        currentQuantity <= tier.quantity_max
      );
    },

    debouncedRecalculatePrinting() {
      // Debounce mechanisme om te voorkomen dat de functie te vaak wordt aangeroepen
      if (this.printingDebounceTimer) {
        clearTimeout(this.printingDebounceTimer);
      }

      this.printingDebounceTimer = setTimeout(() => {
        // Use requestIdleCallback for better performance when available
        if (window.requestIdleCallback) {
          window.requestIdleCallback(
            () => {
              this.recalculatePrinting();
            },
            { timeout: 1000 },
          );
        } else {
          // Fallback for browsers without requestIdleCallback
          this.recalculatePrinting();
        }
        this.printingDebounceTimer = null; // Reset timer na uitvoering
      }, 200); // Further reduced to 200ms for even better responsiveness
    },

    async recalculatePrinting() {
      // Voorkom meerdere gelijktijdige uitvoeringen
      if (this.isPrintingCalculating) {
        return;
      }

      try {
        this.isPrintingCalculating = true;

        // Add timeout to prevent infinite hanging
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => {
            reject(new Error('Printing calculation timeout after 30 seconds'));
          }, 30000);
        });

        const calculationPromise = this.performPrintingCalculation();

        await Promise.race([calculationPromise, timeoutPromise]);
      } catch (error) {
        console.error('Error recalculating printing:', error);
      } finally {
        this.isPrintingCalculating = false;
      }
    },

    async performPrintingCalculation() {
      // Stap 1: Haal producten op
      const allProducts = this.productsByCategory['products']
        .concat(this.productsByCategory['foodDrinks'])
        .concat(this.productsByCategory['packaging']);

      // Stap 2: Bepaal prints per pakket
      const printsPerPakket = calculatePrintingCountsUtil(
        allProducts,
        1, // 1 pakket voor berekening per pakket
        this.productPersonalization,
      );

      // Stap 3: Bepaal totaal aantal prints
      this.totalPrintQty =
        (printsPerPakket.sticker +
          printsPerPakket.zeefdruk +
          printsPerPakket.graveren) *
        this.packageQuantity;

      // Stap 4: Valideer minimum quantity
      const isPrintingAllowed = this.packageQuantity >= this.printingMinQty;

      if (!isPrintingAllowed) {
        // Drukwerk niet toegestaan - reset alles
        this.isPrintingValid = false;
        this.printingValidationMsg = `Drukwerk kan vanaf ${this.printingMinQty} pakketten.`;
        this.printingCounts = { sticker: 0, zeefdruk: 0, graveren: 0 };
        this.printingBreakdown = {
          sticker: { qty: 0, unit: 0, total: 0 },
          zeefdruk: { qty: 0, unit: 0, total: 0 },
          graveren: { qty: 0, unit: 0, total: 0 },
          totalExVat: 0,
        };
        this.printsPerPakketBreakdown = {
          sticker: { qty: 0, unit: 0, total: 0 },
          zeefdruk: { qty: 0, unit: 0, total: 0 },
          graveren: { qty: 0, unit: 0, total: 0 },
          totalExVat: 0,
        };
      } else {
        // Drukwerk toegestaan - bereken kosten
        this.isPrintingValid = true;
        this.printingValidationMsg = '';

        // Bereken counts voor totaal aantal pakketten
        this.printingCounts = calculatePrintingCountsUtil(
          allProducts,
          this.packageQuantity,
          this.productPersonalization,
        );

        // Check if counts have actually changed to avoid unnecessary API calls
        const countsKey = this._getCountsKey(this.printingCounts);
        if (
          this.lastCalculatedCounts === countsKey &&
          this.printingCostsCache.has(countsKey)
        ) {
          this.printingBreakdown = this.printingCostsCache.get(countsKey);
        } else {
          this.printingBreakdown = await this.calculatePrintingCostsWithCache(
            this.printingCounts,
          );
          this.lastCalculatedCounts = countsKey;
        }

        const totalBreakdown = this.printingBreakdown;
        this.printsPerPakketBreakdown = {
          sticker: {
            qty: printsPerPakket.sticker,
            unit: totalBreakdown.sticker.unit,
            total: totalBreakdown.sticker.unit * printsPerPakket.sticker,
          },
          zeefdruk: {
            qty: printsPerPakket.zeefdruk,
            unit: totalBreakdown.zeefdruk.unit,
            total: totalBreakdown.zeefdruk.unit * printsPerPakket.zeefdruk,
          },
          graveren: {
            qty: printsPerPakket.graveren,
            unit: totalBreakdown.graveren.unit,
            total: totalBreakdown.graveren.unit * printsPerPakket.graveren,
          },
          totalExVat:
            totalBreakdown.sticker.unit * printsPerPakket.sticker +
            totalBreakdown.zeefdruk.unit * printsPerPakket.zeefdruk +
            totalBreakdown.graveren.unit * printsPerPakket.graveren,
        };

        // Update personalization met drukwerk data
        this.updatePersonalization();
      }

      // Emit printing updated event
      this.$emit('printing-updated', {
        counts: this.printingCounts,
        breakdown: this.printingBreakdown,
      });
    },

    onProductPersonalizationChanged(newVal, oldVal) {
      if (this._hasPersonalizationChanged(newVal, oldVal)) {
        this.debouncedRecalculatePrinting();
      }
    },

    async calculatePrintingCostsWithCache(counts) {
      const countsKey = this._getCountsKey(counts);

      if (this.printingCostsCache.has(countsKey)) {
        return this.printingCostsCache.get(countsKey);
      }

      const result = await calculatePrintingCostsUtil(counts);

      if (this.printingCostsCache.size > 50) {
        const firstKey = this.printingCostsCache.keys().next().value;
        this.printingCostsCache.delete(firstKey);
      }

      this.printingCostsCache.set(countsKey, result);
      return result;
    },

    // Bulk update method for better performance when selecting multiple products
    updateProductPersonalizationBulk(updates) {
      const updatedPersonalization = { ...this.productPersonalization };

      // Apply all updates at once
      Object.keys(updates).forEach((productId) => {
        updatedPersonalization[productId] = {
          ...updatedPersonalization[productId],
          ...updates[productId],
        };
      });

      this.productPersonalization = updatedPersonalization;

      // Trigger single recalculation for all changes
      this.debouncedRecalculatePrinting();
    },

    // Optimized batch update with requestAnimationFrame
    batchUpdatePersonalization(updates) {
      // Cancel any pending batch update
      if (this._batchUpdateId) {
        cancelAnimationFrame(this._batchUpdateId);
      }

      // Queue the update for the next animation frame
      this._batchUpdateId = requestAnimationFrame(() => {
        this.updateProductPersonalizationBulk(updates);
        this._batchUpdateId = null;
      });
    },

    // Clear all caches when data changes significantly
    clearCaches() {
      this.printingCostsCache.clear();
      this._productsCache = {};
      this.lastCalculatedCounts = null;
    },

    _hasConfigChanged(newConfig, oldConfig) {
      if (!newConfig || !oldConfig) return false;

      const keys = Object.keys(newConfig);
      if (keys.length !== Object.keys(oldConfig).length) return true;

      return keys.some((key) => {
        const newVal = newConfig[key];
        const oldVal = oldConfig[key];

        if (typeof newVal !== typeof oldVal) return true;
        if (typeof newVal === 'object' && newVal !== null) {
          return this._hasConfigChanged(newVal, oldVal);
        }
        return newVal !== oldVal;
      });
    },

    _hasPersonalizationChanged(newVal, oldVal) {
      if (!newVal || !oldVal) return true;

      const newKeys = Object.keys(newVal);
      const oldKeys = Object.keys(oldVal);

      if (newKeys.length !== oldKeys.length) return true;

      return newKeys.some((key) => {
        const newItem = newVal[key];
        const oldItem = oldVal[key];

        if (!newItem || !oldItem) return newItem !== oldItem;

        return (
          newItem.sticker !== oldItem.sticker ||
          newItem.zeefdruk !== oldItem.zeefdruk ||
          newItem.graveren !== oldItem.graveren
        );
      });
    },

    _getCountsKey(counts) {
      return `${counts.sticker || 0}-${counts.zeefdruk || 0}-${counts.graveren || 0}`;
    },
  },
};
</script>
